import _ from 'lodash';
import { AdminFacility } from './admin-facility';
import { AdminRole } from './admin-role';
import { AdminPanel } from './admin-panel';
const PNF = require('google-libphonenumber').PhoneNumberFormat;
const phoneUtil = require('google-libphonenumber').PhoneNumberUtil.getInstance();

export class AdminUser {
  Username = '';
  FirstName = '';
  LastName = '';

  IsNew: boolean;
  IsActive: boolean;
  IsLockedOut: boolean;
  IsMFAEnabled: boolean;
  IsConfirmed: boolean;
  IsSendInvite: boolean;

  IsChanged: boolean;
  IsSelf: boolean;

  OfficePhone: string;
  MobilePhone: string;
  MFAPhone: string;

  NPI: string;
  DEA: string;
  PractitionerId:string;
  Facilities = [];
  Applications = [];
  Panels = [];
  PreviousPanels = [];
  
  AccessRoles = [];
  NotifyRoles = [];

  EnableEmailAlert: boolean;

  static getNewUser(): AdminUser {
    const user = new AdminUser();
    user.IsChanged = false;
    user.IsNew = true;
    user.IsActive = true;
    user.IsLockedOut = false;
    user.IsMFAEnabled = false;
    user.IsConfirmed = false;
    user.IsSendInvite = true;
    user.EnableEmailAlert=false;
    return user;
  }

  constructor() {}

  initialize() {
    // sort roles
    this.Applications = _.sortBy(this.Applications);

    // determine if user is admin or not
    const facCount = this.Facilities ? this.Facilities.length : 0;

    // initialize application roles
    this.AccessRoles = _.filter(this.Applications, a => {
      return a.startsWith('access');
    }).map(a => {
      return a.split('-')[1];
    });

    this.NotifyRoles = _.filter(this.Applications, a => {
      return a.startsWith('notify');
    }).map(a => {
      return a.split('-')[1];
    });

    this.OfficePhone = this.formatPhone(this.OfficePhone);
    this.MobilePhone = this.formatPhone(this.MobilePhone);
    this.MFAPhone = this.formatPhone(this.MFAPhone);
  }

  private formatPhone(phone: string): string {
    try {
      const number = phoneUtil.parseAndKeepRawInput(phone, 'US');
      phone = phoneUtil.format(number, PNF.NATIONAL);
    } catch (err) {}
    return phone;
  }

  trimSpaces() {
    this.Username = _.trim(this.Username);
    this.FirstName = _.trim(this.FirstName);
    this.LastName = _.trim(this.LastName);
    this.OfficePhone = _.trim(this.OfficePhone);
    this.MobilePhone = _.trim(this.MobilePhone);
    this.MFAPhone = _.trim(this.MFAPhone);
    this.NPI = _.trim(this.NPI);
    this.DEA = _.trim(this.DEA);
    this.PractitionerId = _.trim(this.PractitionerId);
  }

  hasMxAdminRole(): boolean {
    return this.hasRole('access-mxadmin');
  }

  isAdmin(): boolean {
    return this.isMxAdmin() || this.isOrgAdmin() || this.isFacAdmin();
  }

  hasParticipantAdminRole(): boolean {
    return this.hasRole('access-participantadmin');
  }

  isMxAdmin(): boolean {
    return this.hasMxAdminRole();
  }

  isOrgAdmin(): boolean {
    return this.hasParticipantAdminRole() && this.getFacilityCount() > 1;
  }

  isFacAdmin(): boolean {
    return this.hasParticipantAdminRole() && this.getFacilityCount() === 1;
  }

  getFullName(): string {
    return this.FirstName + ' ' + this.LastName;
  }

  getFacilityCount(): number {
    return this.Facilities.length;
  }

  hasAccessRoles(): boolean {
    return this.AccessRoles.length > 0;
  }

  hasNotifyRoles(): boolean {
    return this.NotifyRoles.length > 0;
  }

  addFacility(fac: AdminFacility) {
    if (!this.hasFacility(fac)) {
      this.Facilities.push(fac.FacilityCode);
    }
  }

  removeFacility(fac: AdminFacility) {
    _.remove(this.Facilities, f => {
      return f === fac.FacilityCode;
    });
  }

  addRole(app: AdminRole) {
    if (!this.hasRole(app)) {
      this.Applications.push(app.GroupName);
    }
  }

  removeRole(app: AdminRole) {
    _.remove(this.Applications, a => {
      return a === app.GroupName;
    });
  }

  addPanel(panel: AdminPanel) {
    if (!this.hasPanel(panel)) {
      this.Panels.push(panel.GroupName);
    }
  }

  removePanel(panel: AdminPanel) {
    _.remove(this.Panels, p => {
      return p === panel.GroupName;
    });
  }

  isMatch(searchText: string): boolean {
    if (!searchText) {
      return true;
    }
    searchText = searchText.toLowerCase();

    const firstName = this.FirstName ? this.FirstName.toLowerCase() : '';
    const lastName = this.LastName ? this.LastName.toLowerCase() : '';
    const username = this.Username ? this.Username.toLowerCase() : '';
    if (
      firstName.indexOf(searchText) > -1 ||
      lastName.indexOf(searchText) > -1 ||
      username.indexOf(searchText) > -1
    ) {
      return true;
    }

    return _.find(this.Applications, app => {
      // console.log({ app: app, searchText: searchText });
      if (app.toLowerCase().indexOf(searchText) > -1) {
        return true;
      }
    });
  }

  hasFacility(fac: any): boolean {
    if (!fac || !this.Facilities || this.Facilities.length === 0) {
      return false;
    }
    const facilityCode = fac instanceof AdminFacility ? fac.FacilityCode : fac;
    return _.indexOf(this.Facilities, facilityCode) > -1;
  }

  hasRole(role: any): boolean {
    if (!role || !this.Applications || this.Applications.length === 0) {
      return false;
    }
    const roleCode = role instanceof AdminRole ? role.GroupName : role;
    return _.indexOf(this.Applications, roleCode) > -1;
  }

  hasPanel(panel: any): boolean {
    if (!panel || !this.Panels || this.Panels.length === 0) {
      return false;
    }
    const panelCode = panel instanceof AdminPanel ? panel.GroupName : panel;
    return _.indexOf(this.Panels, panelCode) > -1;
  }
}
